import type { Row } from "@tanstack/react-table";
import { useCallback, useMemo } from "react";
import { t } from "ttag";
import _ from "underscore";

import { DateTime } from "metabase/common/components/DateTime";
import { useScrollToTop, useSortingStateChange } from "metabase/common/hooks";
import { MonitorEmptyState } from "metabase/monitor/components/MonitorEmptyState";
import { MonitorTableCard } from "metabase/monitor/components/MonitorTableCard";
import { TaskStatusBadge } from "metabase/monitor/tools/components/TaskStatusBadge";
import { useNavigate } from "metabase/router";
import {
  Ellipsified,
  LoadingOverlay,
  Text,
  TreeTable,
  type TreeTableColumnDef,
  TreeTableSkeleton,
  useTreeTableInstance,
} from "metabase/ui";
import * as Urls from "metabase/urls";
import { EMPTY_CELL_PLACEHOLDER } from "metabase/utils/constants";
import type {
  Database,
  ListTasksSortColumn,
  SortingOptions,
  Task,
} from "metabase-types/api";

import { DEFAULT_SORTING, TASK_SORT_COLUMNS } from "./utils";

const COLUMN_WIDTHS = [0.25, 0.15, 0.12, 0.16, 0.16, 0.1, 0.06];

interface Props {
  databases: Database[];
  isFetching: boolean;
  isLoading: boolean;
  page: number;
  sortingOptions: SortingOptions<ListTasksSortColumn>;
  tasks: Task[];
  onSortingOptionsChange: (
    sortingOptions: SortingOptions<ListTasksSortColumn>,
  ) => void;
}

export const TasksTable = ({
  databases,
  isFetching,
  isLoading,
  page,
  sortingOptions,
  tasks,
  onSortingOptionsChange,
}: Props) => {
  const navigate = useNavigate();

  const databaseByID: Record<number, Database> = useMemo(
    () => _.indexBy(databases, "id"),
    [databases],
  );

  const columns = useMemo(() => getColumns(databaseByID), [databaseByID]);
  const { sortingState, onSortingChange } = useSortingStateChange({
    sortingOptions,
    columns: TASK_SORT_COLUMNS,
    defaultSorting: DEFAULT_SORTING,
    onSortingOptionsChange,
  });

  const handleRowActivate = useCallback(
    (row: Row<Task>) => {
      navigate(Urls.monitorTaskDetails(row.original.id));
    },
    [navigate],
  );

  const treeTableInstance = useTreeTableInstance<Task>({
    data: tasks,
    columns,
    sorting: sortingState,
    manualSorting: true,
    getNodeId: (task) => String(task.id),
    onRowActivate: handleRowActivate,
    onSortingChange,
  });

  useScrollToTop({
    ref: treeTableInstance.containerRef,
    keys: [page, sortingOptions],
    skip: isFetching,
  });

  return (
    <MonitorTableCard aria-busy={isFetching} data-testid="tasks-table">
      {isLoading ? (
        <TreeTableSkeleton columnWidths={COLUMN_WIDTHS} />
      ) : (
        <>
          <LoadingOverlay visible={isFetching} data-testid="loading-overlay" />
          <TreeTable
            instance={treeTableInstance}
            hierarchical={false}
            ariaLabel={t`Tasks`}
            emptyState={<MonitorEmptyState label={t`No results`} />}
            getRowProps={() => ({ "data-testid": "task" })}
            onRowClick={handleRowActivate}
          />
        </>
      )}
    </MonitorTableCard>
  );
};

function getColumns(
  databaseByID: Record<number, Database>,
): TreeTableColumnDef<Task>[] {
  return [
    {
      id: "task",
      header: t`Task`,
      width: "auto",
      minWidth: 200,
      maxAutoWidth: 230,
      enableSorting: true,
      sortDescFirst: false,
      accessorFn: (task) => task.task,
      cell: ({ row }) => <Text>{row.original.task}</Text>,
    },
    {
      id: "db_name",
      header: t`DB Name`,
      width: "auto",
      minWidth: 120,
      maxAutoWidth: 240,
      enableSorting: true,
      sortDescFirst: false,
      accessorFn: (task) =>
        task.db_id !== null ? (databaseByID[task.db_id]?.name ?? "") : "",
      cell: ({ row }) => {
        const { db_id } = row.original;
        // only want unknown if there is a db on the task and we don't have info
        if (db_id === null) {
          return null;
        }
        return databaseByID[db_id]?.name || t`Unknown name`;
      },
    },
    {
      id: "db_engine",
      header: t`DB Engine`,
      width: "auto",
      minWidth: 120,
      maxAutoWidth: 240,
      enableSorting: true,
      sortDescFirst: false,
      accessorFn: (task) =>
        task.db_id !== null ? (databaseByID[task.db_id]?.engine ?? "") : "",
      cell: ({ row }) => {
        const { db_id } = row.original;
        if (db_id === null) {
          return null;
        }
        return databaseByID[db_id]?.engine || t`Unknown engine`;
      },
    },
    {
      id: "started_at",
      header: t`Started at`,
      width: "auto",
      minWidth: 150,
      enableSorting: true,
      sortDescFirst: true,
      accessorFn: (task) => task.started_at,
      cell: ({ row }) => (
        <Ellipsified
          style={{ maxWidth: 180 }}
          alwaysShowTooltip
          tooltip={row.original.started_at}
        >
          <DateTime
            value={row.original.started_at}
            unit="minute"
            data-testid="started-at"
          />
        </Ellipsified>
      ),
    },
    {
      id: "ended_at",
      header: t`Ended at`,
      width: "auto",
      minWidth: 150,
      enableSorting: true,
      sortDescFirst: false,
      accessorFn: (task) => task.ended_at,
      cell: ({ row }) =>
        row.original.ended_at ? (
          <Ellipsified
            style={{ maxWidth: 180 }}
            alwaysShowTooltip
            tooltip={row.original.ended_at}
          >
            <DateTime
              value={row.original.ended_at}
              unit="minute"
              data-testid="ended-at"
            />
          </Ellipsified>
        ) : (
          EMPTY_CELL_PLACEHOLDER
        ),
    },
    {
      id: "duration",
      header: t`Duration (ms)`,
      width: "auto",
      minWidth: 80,
      maxAutoWidth: 100,
      enableSorting: true,
      sortDescFirst: false,
      accessorFn: (task) => task.duration,
      cell: ({ row }) => row.original.duration,
    },
    {
      id: "status",
      header: t`Status`,
      width: "auto",
      minWidth: 100,
      enableSorting: true,
      sortDescFirst: false,
      accessorFn: (task) => task.status,
      cell: ({ row }) => <TaskStatusBadge task={row.original} />,
    },
  ];
}
